import { useEffect, useRef, useState } from "react";
import { Copy, Check, Cpu } from "lucide-react";
import { MarkdownView } from "./MarkdownView";
import type {
  ActivityEntry,
  AgentKey,
  AgentSlot,
  AgentStatus,
  SwarmState,
  ToolCallTrace,
} from "@/lib/swarmTypes";
import { AGENT_META } from "@/lib/swarmTypes";
import { normalizeMarkdown } from "@/lib/markdown";

// Human-readable labels for backend @tool function names emitted via
// the structured AG-UI TOOL_CALL_START events. Kept in sync with the
// backend TOOL_TO_AGENT / _TOOL_STEP_LABELS maps.
const TOOL_LABELS: Record<string, string> = {
  analyze_video_intel: "Analyzing video intel",
  prepare_and_analyze_video: "Processing video stream",
  analyze_document_intel: "Reading documents",
  discover_competitor_documents: "Discovering filings",
  analyze_web_signals: "Scanning web signals",
  scrape_competitor_url: "Scraping competitor page",
  monitor_competitor_news: "Monitoring news feed",
  search_historical_intel: "Searching history",
  store_intel_vector: "Storing intel vector",
};

function toolLabel(name: string): string {
  return TOOL_LABELS[name] ?? name.replace(/_/g, " ");
}

interface AgentSwarmPanelProps {
  agents: SwarmState["agents"];
  isVisible: boolean;
  signals?: { video?: boolean; document?: boolean; web?: boolean; news?: boolean };
}

export function AgentSwarmPanel({ agents, isVisible, signals }: AgentSwarmPanelProps) {
  if (!isVisible) return null;

  const enabledKeys = (Object.keys(AGENT_META) as AgentKey[]).filter(
    (key) => signals == null || signals[key] !== false
  );

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 mb-6">
      {enabledKeys.map((key) => (
        <AgentCard key={key} agentKey={key} slot={agents[key]} />
      ))}
    </div>
  );
}

interface AgentCardProps {
  agentKey: AgentKey;
  slot: AgentSlot;
}

function AgentCard({ agentKey, slot }: AgentCardProps) {
  const meta = AGENT_META[agentKey];
  const { status, startedAt, finishedAt, toolCalls, activityLog, currentStep, streamText } = slot;

  const elapsed =
    status === "running" && startedAt ? Date.now() - startedAt :
    status === "done"    && startedAt && finishedAt ? finishedAt - startedAt :
    null;

  const isRunning = status === "running";
  const isDone    = status === "done";
  const isError   = status === "error";
  const isActive  = isRunning || isDone || isError;

  // Per-agent neon color for the holographic border
  const holoColor = agentKey === "video" ? "#a855f7" :
                    agentKey === "document" ? "#3b82f6" :
                    agentKey === "web" ? "#22d3ee" : "#f59e0b";

  return (
    <div className="relative group min-h-[260px] h-full">
      {/* Holographic border wrapper - rotates conic gradient */}
      <div
        className={[
          "rounded-2xl p-px transition-all duration-700 h-full",
          isRunning ? "animate-holo-border" : "",
        ].join(" ")}
        style={isRunning ? {
          background: `conic-gradient(from var(--holo-angle, 0deg), ${holoColor}, #4a56e8, #a855f7, #22d3ee, ${holoColor})`,
        } : {
          background: isDone ? `linear-gradient(135deg, #22c55e30, #22d3ee10)` :
                      isError ? `linear-gradient(135deg, #ef444430, transparent)` :
                      `linear-gradient(135deg, #1a264030, #0d1829)`,
        }}
      >
        <div
          className={[
            "relative rounded-[15px] overflow-hidden flex flex-col h-full",
            isRunning ? "bg-[#040d1a]" :
            isDone    ? "bg-[#061510]" :
            isError   ? "bg-[#100808]" :
                        "bg-[#080e1c]",
          ].join(" ")}
        >
          {/* Radar pulse rings behind content - only when running */}
          {isRunning && (
            <div className="absolute inset-0 pointer-events-none z-0 flex items-center justify-center overflow-hidden">
              {[0, 0.5, 1.0].map((delay) => (
                <div
                  key={delay}
                  className="absolute rounded-full border border-[#22d3ee]/20 animate-radar"
                  style={{ width: "60px", height: "60px", animationDelay: `${delay}s` }}
                />
              ))}
            </div>
          )}

          {/* CRT scanline overlay */}
          {isRunning && (
            <div
              className="absolute inset-0 pointer-events-none z-0 opacity-[0.03]"
              style={{
                background: "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.8) 2px, rgba(0,0,0,0.8) 4px)",
              }}
            />
          )}

          {/* Scan-line sweep */}
          {isRunning && (
            <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
              <div className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#22d3ee]/60 to-transparent animate-scan" />
            </div>
          )}

          {/* Shimmer sweep on hover */}
          <div
            className="absolute inset-0 pointer-events-none z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 overflow-hidden rounded-[11px]"
          >
            <div
              className="absolute inset-0 animate-shimmer"
              style={{
                background: "linear-gradient(105deg, transparent 35%, rgba(255,255,255,0.04) 50%, transparent 65%)",
                backgroundSize: "200% 100%",
              }}
            />
          </div>

          {/* Done corner accent */}
          {isDone && (
            <div className="absolute top-0 right-0 pointer-events-none z-0">
              <div className="w-px h-8 absolute top-0 right-2 bg-gradient-to-b from-emerald-400/60 to-transparent" />
              <div className="h-px w-8 absolute top-2 right-0 bg-gradient-to-l from-emerald-400/60 to-transparent" />
              <div className="w-1.5 h-1.5 absolute top-1.5 right-1.5 bg-emerald-400/40 rounded-full" />
            </div>
          )}

          <div className="relative z-10 p-5 flex flex-col gap-3 flex-1 min-h-0">
            {/* ── Header row ── */}
            <div className="flex items-center gap-3">
              <AgentIcon agentKey={agentKey} status={status} />
              <div className="flex-1 min-w-0">
                <p className={[
                  "text-base font-semibold truncate leading-tight",
                  isRunning ? "animate-glitch" : "",
                  labelColor(status),
                ].join(" ")}>
                  {meta.label}
                </p>
                <p className="text-xs text-slate-500 leading-tight truncate mt-0.5">{meta.subtitle}</p>
              </div>
              {elapsed !== null && (
                <span className="text-sm font-mono tabular-nums shrink-0 text-slate-500">
                  {formatMs(elapsed)}
                </span>
              )}
            </div>

            {/* ── Powered-by-Amazon-Nova badge ──
                Tasteful model branding for the AWS Builder Center audience: the
                product-facing Nova model NAME only, never a raw model ID. */}
            <div
              className="inline-flex w-fit items-center gap-1.5 rounded-full border border-[#22d3ee]/20 bg-[#22d3ee]/[0.06] px-2 py-0.5"
              title={`Powered by ${meta.model} on Amazon Bedrock AgentCore`}
            >
              <Cpu className="w-3 h-3 text-[#22d3ee]/80 shrink-0" aria-hidden="true" />
              <span className="text-[10px] font-medium text-slate-400">
                Powered by <span className="text-[#22d3ee]/90">{meta.model}</span>
                <span className="text-slate-500"> on Bedrock AgentCore</span>
              </span>
            </div>

            {/* ── Progress bar ── */}
            {isRunning && (
              <div className="h-0.5 bg-[#1a2640] rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full animate-indeterminate-bar"
                  style={{
                    background: `linear-gradient(90deg, #4a56e8, ${holoColor}, #a855f7, ${holoColor})`,
                    backgroundSize: "200% 100%",
                  }}
                />
              </div>
            )}

            {/* ── Activity feed ── */}
            {isActive && (
              <ActivityFeed
                log={activityLog}
                currentStep={currentStep}
                status={status}
                toolCalls={toolCalls}
                streamText={streamText}
                agentKey={agentKey}
                isDone={isDone}
              />
            )}

            {/* ── Status footer ── */}
            <div className="flex items-center gap-1.5">
              <span
                className={[
                  "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider",
                  statusChip(status),
                ].join(" ")}
              >
                <StatusDot status={status} />
                {statusLabel(status)}
              </span>
              {isDone && toolCalls.length > 0 && (
                <span className="ml-auto text-[9px] text-slate-600 font-mono">
                  {toolCalls.length} source{toolCalls.length !== 1 ? "s" : ""} checked
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Activity Feed ──────────────────────────────────────────────────────────────

interface ActivityFeedProps {
  log: ActivityEntry[];
  currentStep: string;
  status: AgentStatus;
  toolCalls: ToolCallTrace[];
  streamText: string;
  agentKey: AgentKey;
  isDone: boolean;
}

function ActivityFeed({ log, currentStep, status, toolCalls, streamText, isDone }: ActivityFeedProps) {
  const isRunning = status === "running";
  const feedRef = useRef<HTMLDivElement>(null);
  const streamRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  async function copyCard() {
    try {
      await navigator.clipboard.writeText(normalizeMarkdown(streamText));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable - no-op */
    }
  }

  useEffect(() => {
    const el = feedRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [log.length]);

  useEffect(() => {
    const el = streamRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [streamText]);

  // Hide step progress entries once done - only content entries (tool calls, errors) remain
  const visible = (isDone ? log.filter(e => e.kind !== "step") : log).slice(-8);

  return (
    <div className="rounded-xl bg-[#050b16] border border-[#1a2640]/60 overflow-hidden flex-1 flex flex-col min-h-0">
      {/* Structured tool-call cards - one labeled header per TOOL_CALL_START */}
      {toolCalls.length > 0 && (
        <div className="px-2.5 py-2 flex flex-col gap-1.5 border-b border-[#1a2640]/40">
          {toolCalls.slice(-4).map((tc) => (
            <ToolCallCard key={tc.toolCallId} call={tc} />
          ))}
        </div>
      )}

      {/* Current step indicator */}
      {isRunning && currentStep && (
        <div className="flex items-center gap-1.5 px-3 py-2 border-b border-[#1a2640]/40">
          <span className="w-1.5 h-1.5 rounded-full bg-[#22d3ee] animate-ping shrink-0" />
          <span className="text-xs text-[#22d3ee]/90 font-medium break-words leading-snug">
            {currentStep}
          </span>
        </div>
      )}

      {/* Live streaming agent output - shown while running AND after completion */}
      {streamText && (
        <div
          ref={streamRef}
          className="relative overflow-y-auto flex-1 min-h-[180px] max-h-[460px] px-4 py-3 border-b border-[#1a2640]/30 select-text scrollbar-thin"
        >
          {/* Sticky header: label + always-available Copy (readable + copyable in realtime) */}
          <div className="sticky top-0 z-10 -mx-4 -mt-3 mb-2.5 px-4 py-2 flex items-center justify-between bg-[#050b16]/95 backdrop-blur border-b border-[#1a2640]/40">
            <span className="text-[10px] text-slate-500 font-mono uppercase tracking-widest">
              {isDone ? "Analysis" : "Live output"}
            </span>
            <button
              onClick={copyCard}
              title="Copy this card"
              aria-label="Copy this agent's output"
              className="flex items-center gap-1 text-[10px] text-slate-500 hover:text-cyan-300 transition-colors cursor-pointer min-h-[28px] px-1"
            >
              {copied ? (
                <><Check className="w-3 h-3 text-emerald-400" />Copied</>
              ) : (
                <><Copy className="w-3 h-3" />Copy</>
              )}
            </button>
          </div>
          <div className="relative">
            <MarkdownView variant="compact">{streamText}</MarkdownView>
            {isRunning && (
              <span className="inline-block w-1 h-3 bg-[#22d3ee]/70 ml-0.5 animate-pulse align-middle" />
            )}
          </div>
        </div>
      )}

      {/* Activity log entries */}
      {visible.length > 0 ? (
        <div
          ref={feedRef}
          className="overflow-y-auto max-h-[160px] px-3 py-2 flex flex-col gap-1.5 scrollbar-none"
        >
          {visible.map((entry, i) => (
            <LogEntry key={`${entry.ts}-${i}`} entry={entry} />
          ))}
        </div>
      ) : !streamText ? (
        isRunning ? (
          <div className="px-3.5 py-3 flex flex-col gap-2.5" role="status" aria-label="Agent is starting up">
            <div className="flex items-center gap-1.5">
              <div className="flex gap-0.5 items-end h-2.5">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="w-0.5 bg-[#22d3ee]/50 rounded-full animate-wave"
                    style={{ height: "100%", animationDelay: `${i * 0.1}s` }} />
                ))}
              </div>
              <span className="text-[10px] text-slate-500 font-mono">Spinning up…</span>
            </div>
            {/* Skeleton lines while waiting for first token */}
            <div className="space-y-1.5">
              <div className="h-2 rounded bg-white/5 animate-pulse" style={{ width: "92%" }} />
              <div className="h-2 rounded bg-white/5 animate-pulse" style={{ width: "78%", animationDelay: "0.15s" }} />
              <div className="h-2 rounded bg-white/5 animate-pulse" style={{ width: "85%", animationDelay: "0.3s" }} />
            </div>
          </div>
        ) : (
          <div className="px-2.5 py-2">
            <span className="text-[10px] text-slate-700">No activity recorded</span>
          </div>
        )
      ) : null}
    </div>
  );
}

function LogEntry({ entry }: { entry: ActivityEntry }) {
  const { text, kind } = entry;

  const isInfo = kind === "info";

  const dotColor =
    isInfo           ? "bg-slate-700/50" :
    kind === "done"  ? "bg-emerald-400" :
    kind === "tool"  ? "bg-[#22d3ee]" :
    kind === "step"  ? "bg-brand-400" :
    kind === "error" ? "bg-red-400" :
    "bg-slate-600";

  const textColor =
    isInfo           ? "text-slate-600" :
    kind === "done"  ? "text-emerald-400/80" :
    kind === "tool"  ? "text-[#22d3ee]/80" :
    kind === "step"  ? "text-brand-300/80" :
    kind === "error" ? "text-red-400/80" :
    "text-slate-500";

  return (
    <div className={`flex items-start gap-1.5 min-w-0 animate-matrix-rain${isInfo ? " pl-4" : ""}`}>
      {isInfo
        ? <span className="text-[10px] text-slate-600 shrink-0 mt-px font-mono">↳</span>
        : <span className={`w-1.5 h-1.5 rounded-full shrink-0 mt-0.5 ${dotColor}`} />
      }
      <span className={`text-[11px] font-mono leading-snug break-all ${textColor}`}>
        {isInfo && text.startsWith("↳ ") ? text.slice(2) : text}
      </span>
    </div>
  );
}

// ── Tool Call Card ───────────────────────────────────────────────────────────────

/**
 * Renders a single backend tool call (TOOL_CALL_START → TOOL_CALL_END) as a
 * labeled card header: a status icon, the human-readable tool name, and a
 * running/done badge. Replaces the old "raw text only" feed for tool activity.
 */
function ToolCallCard({ call }: { call: ToolCallTrace }) {
  const running = call.finishedAt === null;
  const elapsed = call.finishedAt !== null ? call.finishedAt - call.startedAt : null;

  return (
    <div
      className="flex items-center gap-2 rounded-md bg-[#0a1322] border border-[#1a2640]/70 px-2.5 py-1.5 transition-colors duration-200 hover:border-[#22d3ee]/40"
      role="status"
      aria-label={`Tool ${toolLabel(call.name)} ${running ? "running" : "complete"}`}
    >
      {running ? (
        <span className="relative flex w-2 h-2 shrink-0" aria-hidden="true">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#22d3ee] opacity-60" />
          <span className="relative inline-flex rounded-full w-2 h-2 bg-[#22d3ee]" />
        </span>
      ) : (
        <svg
          className="w-3 h-3 shrink-0 text-emerald-400"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
        </svg>
      )}
      <span
        className={[
          "text-[11px] font-medium truncate flex-1 min-w-0 transition-colors duration-200",
          running ? "text-[#22d3ee]/90" : "text-slate-400",
        ].join(" ")}
      >
        {toolLabel(call.name)}
      </span>
      <span
        className={[
          "text-[9px] font-mono uppercase tracking-wider shrink-0 px-1.5 py-px rounded transition-colors duration-200",
          running
            ? "text-[#22d3ee]/80 bg-[#22d3ee]/10"
            : "text-emerald-400/70 bg-emerald-400/10",
        ].join(" ")}
      >
        {running ? "running" : elapsed !== null ? formatMs(elapsed) : "done"}
      </span>
    </div>
  );
}

// ── Agent Icon ─────────────────────────────────────────────────────────────────

function AgentIcon({ agentKey, status }: { agentKey: AgentKey; status: AgentStatus }) {
  const isActive  = status !== "idle";
  const isRunning = status === "running";

  const containerClass = [
    "relative w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-all duration-500",
    isRunning ? "animate-neon-pulse" : "",
    isRunning ? "animate-glow-ring" : "",
    isActive ? iconBg(agentKey) : "bg-[#111827] border border-[#1a2640]",
  ].join(" ");

  const iconClass = [
    "w-5 h-5 transition-colors duration-300",
    isActive ? iconColor(agentKey) : "text-slate-700",
    isRunning ? "animate-rgb-split" : "",
  ].join(" ");

  return (
    <div className={containerClass}>
      {agentKey === "video" && (
        <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
        </svg>
      )}
      {agentKey === "document" && (
        <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
        </svg>
      )}
      {agentKey === "web" && (
        <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
        </svg>
      )}
      {agentKey === "news" && (
        <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 01-2.25 2.25M16.5 7.5V18a2.25 2.25 0 002.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 002.25 2.25h13.5M6 7.5h3v3H6v-3z" />
        </svg>
      )}
    </div>
  );
}

// ── Status Dot ─────────────────────────────────────────────────────────────────

function StatusDot({ status }: { status: AgentStatus }) {
  if (status === "running") {
    return (
      <span className="relative flex w-2 h-2 shrink-0">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#22d3ee] opacity-55" />
        <span className="relative inline-flex rounded-full w-2 h-2 bg-[#22d3ee]" />
      </span>
    );
  }
  const colors: Record<AgentStatus, string> = {
    idle:    "bg-slate-800",
    running: "bg-[#22d3ee]",
    done:    "bg-emerald-400",
    error:   "bg-red-400",
  };
  return <span className={`w-2 h-2 rounded-full shrink-0 ${colors[status]}`} />;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function iconBg(key: AgentKey): string {
  const map: Record<AgentKey, string> = {
    video:    "bg-purple-950/50 border border-purple-700/30",
    document: "bg-blue-950/50 border border-blue-700/30",
    web:      "bg-cyan-950/50 border border-cyan-700/30",
    news:     "bg-amber-950/50 border border-amber-700/30",
  };
  return map[key] ?? "bg-slate-900";
}

function iconColor(key: AgentKey): string {
  const map: Record<AgentKey, string> = {
    video:    "text-purple-400",
    document: "text-blue-400",
    web:      "text-cyan-400",
    news:     "text-amber-400",
  };
  return map[key] ?? "text-slate-400";
}

function labelColor(s: AgentStatus): string {
  switch (s) {
    case "running": return "text-[#22d3ee]";
    case "done":    return "text-emerald-400";
    case "error":   return "text-red-400";
    default:        return "text-slate-600";
  }
}

function statusLabel(s: AgentStatus): string {
  switch (s) {
    case "running": return "Live";
    case "done":    return "Complete";
    case "error":   return "Error";
    default:        return "Queued";
  }
}

function statusChip(s: AgentStatus): string {
  switch (s) {
    case "running": return "border-[#22d3ee]/30 bg-[#22d3ee]/10 text-[#22d3ee]";
    case "done":    return "border-emerald-700/40 bg-emerald-500/10 text-emerald-400";
    case "error":   return "border-red-800/40 bg-red-500/10 text-red-400";
    default:        return "border-[#1a2640] bg-white/5 text-slate-500";
  }
}

function formatMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}
